import { useMemo } from 'react'
import { TagRow, type TagRowPicker } from '@/components/builder/TagRow'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TagDefinition, TagDefinitionsState } from '@/hooks/useLinkBuilderDataEntry'
import {
  chainAncestors,
  chainDescendants,
  orderByChain,
  type PickerBinding,
} from '@/lib/pickerBindings'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'
import type { TagEntry } from '@/lib/tagEntry'

const conjunction = new Intl.ListFormat('en', { type: 'conjunction' })

interface AnalyticsTagSectionProps {
  state: TagDefinitionsState
  tags: TagEntry[]
  pickerBindings: ReadonlyMap<string, PickerBinding>
  fetchOptions: FetchPickerOptions
  focusTagId: string | null
  onAdd: (queryKey: string) => void
  onChangeValue: (id: string, value: string) => void
  onPick: (id: string, option: PickerOption) => void
  onRemove: (id: string) => void
}

export function AnalyticsTagSection({
  state,
  tags,
  pickerBindings,
  fetchOptions,
  focusTagId,
  onAdd,
  onChangeValue,
  onPick,
  onRemove,
}: AnalyticsTagSectionProps) {
  const definitionsByQueryKey = useMemo<Map<string, TagDefinition>>(() => {
    if (state.status !== 'ready') return new Map()
    return new Map(state.definitions.map((d) => [d.queryKey, d]))
  }, [state])

  const myTags = useMemo(
    () => orderByChain(tags.filter((t) => definitionsByQueryKey.has(t.key)), pickerBindings),
    [tags, definitionsByQueryKey, pickerBindings],
  )

  const available = useMemo(() => {
    if (state.status !== 'ready') return []
    const picked = new Set(myTags.map((t) => t.key))
    return state.definitions
      .filter((d) => !picked.has(d.queryKey))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [state, myTags])

  const pickerFor = (tag: TagEntry, binding: PickerBinding): TagRowPicker => {
    const parent =
      binding.parentKey === null ? undefined : tags.find((t) => t.key === binding.parentKey)
    const picked = parent?.pickedValue
    const group = picked === undefined || picked === '' ? null : picked
    return {
      source: binding.source,
      group,
      awaitedParent:
        binding.parentKey === null || group !== null
          ? null
          : (definitionsByQueryKey.get(binding.parentKey)?.name ?? binding.parentKey),
      fetchOptions,
      onPick: (option) => onPick(tag.id, option),
    }
  }

  const siblingCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const tag of myTags) {
      counts.set(tag.key, (counts.get(tag.key) ?? 0) + 1)
    }
    return counts
  }, [myTags])

  const missingAncestorsOf = (queryKey: string): string[] =>
    chainAncestors(queryKey, pickerBindings)
      .filter((key) => !myTags.some((t) => t.key === key))
      .map((key) => definitionsByQueryKey.get(key)?.name ?? key)

  const removeLabelFor = (tag: TagEntry): string => {
    const going = chainDescendants(tag.key, pickerBindings).filter((key) =>
      myTags.some((t) => t.key === key),
    )
    const names = [tag.key, ...going].map((key) => definitionsByQueryKey.get(key)?.name ?? key)
    return `Remove ${conjunction.format(names)}`
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium">Engage Analytics</h2>

      {state.status === 'loading' && (
        <p className="text-xs text-muted-foreground">Loading tags…</p>
      )}

      {state.status === 'error' && (
        <p className="text-xs text-muted-foreground">
          Couldn’t load tracking tags. UTM and custom parameters still work.
        </p>
      )}

      {state.status === 'ready' && state.definitions.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No Engage Analytics tags have been configured yet.
        </p>
      )}

      {state.status === 'ready' && state.definitions.length > 0 && (
        <>
          <div className="space-y-2.5">
            {myTags.map((tag) => {
              const def = definitionsByQueryKey.get(tag.key)
              if (!def) return null
              const binding = pickerBindings.get(tag.key)
              return (
                <TagRow
                  key={tag.id}
                  label={def.name}
                  queryKey={def.queryKey}
                  description={def.description}
                  value={tag.value}
                  onChange={(value) => onChangeValue(tag.id, value)}
                  onRemove={() => onRemove(tag.id)}
                  picker={binding && pickerFor(tag, binding)}
                  pickedValue={binding && binding.idKey !== null ? tag.pickedValue : undefined}
                  removeLabel={removeLabelFor(tag)}
                  autoFocus={tag.id === focusTagId}
                  collidesWithSibling={(siblingCounts.get(tag.key) ?? 0) > 1}
                />
              )
            })}
          </div>

          {available.length > 0 ? (
            <Select value="" onValueChange={onAdd}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Add an Engage Analytics parameter…" />
              </SelectTrigger>
              <SelectContent>
                {available.map((d) => {
                  const alsoAdds = missingAncestorsOf(d.queryKey)
                  return (
                    <SelectItem key={d.id} value={d.queryKey} className="flex-col items-start">
                      <span>
                        {d.name}{' '}
                        <span className="text-muted-foreground">— {d.queryKey}</span>
                      </span>
                      {alsoAdds.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Also adds {conjunction.format(alsoAdds)}
                        </span>
                      )}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-xs text-muted-foreground">All tags added.</p>
          )}
        </>
      )}
    </section>
  )
}
